import { useTranslation } from "react-i18next";
import { Button, Icon, Text } from "@ucl/react-library";
import { useLocation, useNavigate } from "react-router-dom";
import { thankYouPageConfig } from "./thank-you-page-config";
import { CaseInfoBanner } from "src/components/case-info-banner/case-info-banner";
import { QuestionsAboutScheme } from "src/components/questions-about-scheme/questions-about-scheme";

const SUPPORT_PHONE_NUMBER = "03457125490";
const SUPPORT_EMAIL_ADDRESS = "financeredress@ford.com";

const SUPPORT_PHONE_HREF = `tel:${SUPPORT_PHONE_NUMBER}`;
const SUPPORT_EMAIL_HREF = `mailto:${SUPPORT_EMAIL_ADDRESS}`;

export type ThankYouPageVariants =
  | "optedIn"
  | "optedOut"
  | "documentsSubmitted"
  | "contactDetailsUpdatedEmail"
  | "contactDetailsUpdatedPost"
  | "challengeSubmitted"
  | "objectionSubmitted"
  | "noRedressAccepted"
  | "redressAccepted"
  | "bankDetailsNameValidationFailed";

type ThankYouPageProps = {
  pageType?: ThankYouPageVariants;
};

export const ThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as ThankYouPageProps | null;
  const pageType = state?.pageType ?? "optedIn";

  const config = thankYouPageConfig[pageType];

  const { t } = useTranslation("translation", {
    keyPrefix: `thankYouPage.${config.translationKey}`,
  });

  const { t: genericT } = useTranslation();

  const handleBack = () => {
    navigate("/case");
  };

  const handleCall = () => {
    window.location.assign(SUPPORT_PHONE_HREF);
  };

  const handleEmail = () => {
    window.location.assign(SUPPORT_EMAIL_HREF);
  };

  const showCallButton = "showCallButton" in config ? config.showCallButton : false;

  const showEmailButton = "showEmailButton" in config ? config.showEmailButton : false;

  return (
    <div className="flex flex-col text-center gap-10 px-5 py-[30px] md:p-10 max-w-[600px]">
      <div className="flex flex-col gap-4">
        <div className="self-center">
          <Icon icon={config.iconType} color={config.iconColour} extended size="xxl" />
        </div>

        <div className="flex flex-col gap-2">
          <Text.H1 fontWeight="semi-bold">{t("title")}</Text.H1>
          <Text.P size="large">{t("subtitle")}</Text.P>
        </div>
      </div>

      <CaseInfoBanner translationKey={`thankYouPage.${config.translationKey}`} />

      {showCallButton && (
        <Button onClick={handleCall} fullWidth>
          {t("callButton")}
        </Button>
      )}

      {showEmailButton && (
        <Button onClick={handleEmail} variant="secondary" fullWidth>
          {t("emailButton")}
        </Button>
      )}

      {config.showBackButton && (
        <div className="text-start">
          <Button onClick={handleBack}>{genericT("thankYouPage.backToDetails")}</Button>
        </div>
      )}

      <QuestionsAboutScheme />
    </div>
  );
};

export default ThankYouPage;
